<template>
  <div class="invoices-section">
    <div class="section-header">
      <div class="section-title">
        <h3>Számlák</h3>
        <span class="section-count">{{ invoiceCount }}db számla</span>
      </div>
      <div class="section-actions">
        <select class="form-select form-select-sm" v-model="month" @change="loadInvoices" :disabled="loading">
          <option v-for="m in months" :key="m.value" :value="m.value">{{ m.label }}</option>
        </select>
        <button class="btn btn-primary btn-sm" @click="downloadInvoices" :disabled="loading || invoiceCount === 0">
          <i class="fas fa-download"></i>
          Letöltés
        </button>
      </div>
    </div>

    <div class="section-content">
      <div v-if="loading" class="loading-spinner">
        <i class="fas fa-spinner fa-spin"></i>
        <span>Számlák betöltése...</span>
      </div>
      <div v-else-if="error" class="error-state">
        <i class="fas fa-exclamation-triangle"></i>
        <p>Hiba történt a számlák betöltése során</p>
        <button class="btn btn-primary btn-sm" @click="loadInvoices">
          <i class="fas fa-redo"></i>
          Újrapróbálás
        </button>
      </div>
      <div v-else-if="invoices.length === 0" class="empty-state">
        <i class="fas fa-inbox"></i>
        <p>Nincs számla ebben a hónapban</p>
      </div>
      <div v-else class="invoices-list">
        <div v-for="invoice in invoices" :key="invoice.id" class="invoice-item" @click="downloadInvoice(invoice.id)">
          <div class="invoice-info">
            <h6 class="invoice-title">
              {{ invoice.invoice_number }}
              <small class="invoice-type">({{ invoice.type }})</small>
            </h6>
            <div class="invoice-details">
              <span class="invoice-amount">{{ formatCurrency(invoice.gross_total) }}</span>
              <span class="invoice-status" :class="invoice.status">
                <i class="fas fa-circle" v-if="invoice.status === 'paid'"></i>
                <i class="fas fa-clock" v-else-if="invoice.status === 'pending'"></i>
                {{ invoice.status }}
              </span>
              <span class="invoice-date">
                <i class="fas fa-calendar-alt"></i>
                {{ formatDate(invoice.created_at) }}
              </span>
            </div>
          </div>
          <div class="invoice-actions">
            <button class="btn btn-outline-primary btn-sm" @click.stop="downloadInvoice(invoice.id)">
              <i class="fas fa-download"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="section-footer" v-if="notShownCount > 0">
        <router-link to="/invoices" class="btn btn-outline-primary">
          <i class="fas fa-eye me-2"></i>
          További {{ notShownCount }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';

export default {
  name: 'InvoicesCard',
  data() {
    return {
      invoices: [],
      invoiceCount: 0,
      notShownCount: 0,
      loading: false,
      error: false,
      months: [],    // available months for selection
      month: null    // selected month as YYYY-MM
    }
  },
  async mounted() {
    // allow selecting the current month and the previous 6 months
    const now = new Date();
    const months = [];

    for (let offset = 0; offset <= 6; offset++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - offset, 1);
      const year = monthDate.getFullYear();
      const month = String(monthDate.getMonth() + 1).padStart(2, '0');
      const value = `${year}-${month}`;
      const label = `${monthDate.toLocaleString('hu-HU', { month: 'long' })} ${year}`;
      months.push({ value, label });
    }

    this.months = months;
    this.month = months.length ? months[0].value : null; // default = current month

    await this.loadInvoices();
  },
  methods: {
    async loadInvoices() {
      try {
        this.loading = true;
        this.error = false;

        // use monthly endpoint with month param
        const response = await apiService.get(`/invoices/monthly?month=${this.month}`);

        // response format expected:
        // {
        //  "invoices": [...],
        //  "summary": { "total": { "count", "amount" }, ... }
        // }
        const payload = response && response.data ? response.data : response;
        if (payload && (payload.invoices || payload.summary)) {
          this.invoices = payload.invoices || [];
          this.invoiceCount = (payload.summary && payload.summary.total && payload.summary.total.count) || this.invoices.length;
          this.notShownCount = Math.max(0, this.invoiceCount - this.invoices.length);
        } else {
          throw new Error('Failed to load invoices');
        }
      } catch (err) {
        console.error('Error loading invoices:', err);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('hu-HU');
    },
    formatCurrency(amount) {
      if (amount == null) return '-';
      const num = Number(amount);
      return new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: 'HUF',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(num);
    },
    viewInvoice(invoiceId) {
      this.$router.push(`/invoices/${invoiceId}`);
    },
    createNewInvoice() {
      this.$router.push('/invoices/create');
    },
    async downloadInvoice(invoiceId) {
      try {
        await apiService.get(`/invoices/${invoiceId}/download`, { responseType: 'blob' })
          .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice_${invoiceId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
          })
          .catch((error) => {
            console.error('Error downloading invoice:', error);
            this.$emit('show-alert', { type: 'error', message: 'Hiba történt a számla letöltése során.' });
          }).finally(() => {
            this.loading = false
          });
      } catch (err) {
        console.error('Error downloading invoice:', err);
        this.$swal.fire({
          title: 'Hiba',
          text: 'Nem sikerült letölteni a számlát',
          icon: 'error'
        });
      }
    },
    async downloadInvoices() {
      try {
        // request binary download from monthly download endpoint with month param
        const resp = await apiService.get(`/invoices/monthly/download?month=${this.month}`, { responseType: 'blob' });

        // Handle axios-like or fetch-like result
        let blob;
        if (resp && resp.data instanceof Blob) {
          blob = resp.data;
        } else if (resp instanceof Blob) {
          blob = resp;
        } else if (resp && typeof resp.blob === 'function') {
          blob = await resp.blob();
        } else {
          // unknown response shape => try to create blob from resp.data
          const data = resp && resp.data ? resp.data : resp;
          blob = new Blob([data]);
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoices-${this.month}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Error downloading invoices:', err);
        // fallback: open endpoint in new tab (may trigger browser download)
        try {
          window.open(`/invoices/monthly/download?month=${this.month}`, '_blank');
        } catch (e) {
          // ignore
        }
      }
    }
  }
}
</script>

<style scoped>
.invoices-section {
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.section-title h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  margin-bottom: 0.25rem;
}

.section-count {
  font-size: 0.875rem;
  color: #6c757d;
}

.section-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.section-content {
  background: transparent;
}

.invoices-list {
  padding-top: 10px;
   overflow-y: auto;
}

.invoice-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.invoice-item:hover {
  border-color: #1d4699;
  box-shadow: 0 5px 20px rgba(29, 70, 153, 0.15);
  transform: translateY(-2px);
}

.invoice-item:last-child {
  margin-bottom: 0;
}

.invoice-info {
  flex: 1;
  min-width: 0;
  margin-right: 1rem;
}

.invoice-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  line-height: 1.3;
}

.invoice-type {
  font-weight: 400;
  font-size: 0.875rem;
  color: #6c757d;
}

.invoice-details {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.invoice-amount {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2c3e50;
}

.invoice-status {
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.invoice-status.paid {
  color: #28a745;
}

.invoice-status.pending {
  color: #ffc107;
}

.invoice-date {
  font-size: 0.75rem;
  color: #1d4699;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.invoice-actions {
  flex-shrink: 0;
}

.section-footer {
  margin-top: 1.5rem;
  text-align: center;
}

.loading-spinner,
.error-state,
.empty-state {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

.loading-spinner i,
.error-state i,
.empty-state i {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.loading-spinner {
  color: #1d4699;
}

.loading-spinner i {
  animation: spin 1s linear infinite;
}

.error-state {
  color: #dc3545;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .section-actions {
    width: 100%;
    justify-content: space-between;
  }

  .invoice-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .invoice-info {
    margin-right: 0;
  }

  .invoice-actions {
    align-self: flex-end;
  }
}
</style>
